import Photon from 'photon-realtime';
import { matchBosses, matchDeck, type SideLoadout } from '../meta/profile';
import type { BoardMode, Side } from '../data/types';
import type { FoundMatch, NetCommand, NetLink } from './types';

const REGION = 'eu';
const HELLO = 10;
const START = 11;
const CMD = 12;
const PING = 13;
const CACHE = 4;

interface HelloBody {
  commander: string;
  levels: string;
  crit: number;
  deck: string;
  bosses: string;
}

interface PhotonActor {
  actorNr: number;
}

interface PhotonClient {
  connectToRegionMaster: (region: string) => void;
  joinRandomOrCreateRoom: (joinOpts: object, name: string, createOpts: object) => boolean;
  raiseEvent: (code: number, data: unknown, opts?: { cache?: number }) => void;
  disconnect: () => void;
  myActor: () => PhotonActor;
  myRoom: () => { name: string };
  onStateChange: (state: number) => void;
  onEvent: (code: number, data: unknown, actorNr: number) => void;
  onError: (code: number, message: string) => void;
  onJoinRoom: () => void;
}

interface PhotonModule {
  LoadBalancing: { LoadBalancingClient: new (protocol: number, appId: string, version: string) => PhotonClient };
  ConnectionProtocol: { Wss: number };
  PhotonPeer: { setWebSocketImpl: (impl: typeof WebSocket) => void };
}

const SDK = Photon as PhotonModule;
if (typeof WebSocket !== 'undefined') SDK.PhotonPeer.setWebSocketImpl(WebSocket);

function parseList(raw: string | undefined): string[] {
  try {
    const value = JSON.parse(raw ?? '[]') as unknown;
    return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function loadoutOf(body: HelloBody): SideLoadout {
  let levels: Record<string, number> = {};
  try {
    levels = JSON.parse(body.levels) as Record<string, number>;
  } catch {
    levels = {};
  }
  return { levels, crit: body.crit };
}

/**
 * Photon Cloud matchmaking. joinRandomOrCreateRoom pairs any two devices that
 * have the page open, including a phone and an iPad.
 */
export function startPhotonSearch(opts: {
  appId: string;
  commander: string;
  loadout: SideLoadout;
  deck: string[];
  bosses: string[];
  board?: BoardMode;
  onFound: (match: FoundMatch) => void;
  onStatus: (detail: string) => void;
}): () => void {
  const client = new SDK.LoadBalancing.LoadBalancingClient(SDK.ConnectionProtocol.Wss, opts.appId, '1');
  let handed = false;
  let stopped = false;
  let other: (HelloBody & { actor: number }) | null = null;
  let started = false;
  const mine: HelloBody = {
    commander: opts.commander,
    levels: JSON.stringify(opts.loadout.levels),
    crit: opts.loadout.crit,
    deck: JSON.stringify(opts.deck),
    bosses: JSON.stringify(opts.bosses),
  };

  const stop = (): void => {
    if (stopped || handed) return;
    stopped = true;
    try {
      client.disconnect();
    } catch {
      /* already closed */
    }
  };

  const go = (side: Side, seed: number, startAt: number, opp: HelloBody, board: BoardMode): void => {
    if (started || stopped) return;
    started = true;
    handed = true;
    const opponent = loadoutOf(opp);
    const self = loadoutOf(mine);
    const mineDeck = matchDeck(parseList(mine.deck));
    const mineBosses = matchBosses(parseList(mine.bosses));
    const theirDeck = matchDeck(parseList(opp.deck));
    const theirBosses = matchBosses(parseList(opp.bosses));
    opts.onFound({
      matchId: client.myRoom().name || `photon:${seed}`,
      seed,
      board,
      loadout: side === 0 ? [self, opponent] : [opponent, self],
      decks: side === 0 ? [mineDeck, theirDeck] : [theirDeck, mineDeck],
      bosses: side === 0 ? [mineBosses, theirBosses] : [theirBosses, mineBosses],
      link: photonLink(client, side, startAt, opp.commander),
    });
  };

  client.onError = () => {
    if (!stopped && !handed) opts.onStatus('Photon could not connect. Still searching.');
  };

  client.onEvent = (code, data, actorNr) => {
    if (stopped || started) return;
    if (code === HELLO && data && typeof data === 'object') {
      const body = data as HelloBody;
      if (!body.commander || actorNr === client.myActor().actorNr) return;
      other = { ...body, actor: actorNr };
      const me = client.myActor().actorNr;
      if (me > 0 && me < actorNr) {
        const seed = (Date.now() % 2147483646) + 1;
        const startAt = Date.now() + 2000;
        const board: BoardMode = opts.board === 'painted' ? 'painted' : 'arrows';
        client.raiseEvent(START, { seed, startAt, board });
        go(0, seed, startAt, body, board);
      }
      return;
    }
    if (code === START && data && typeof data === 'object' && other) {
      const start = data as { seed?: number; startAt?: number; board?: string };
      if (start.seed && start.startAt) go(1, start.seed, start.startAt, other, start.board === 'painted' ? 'painted' : 'arrows');
    }
  };

  client.onJoinRoom = () => {
    if (stopped) return;
    client.raiseEvent(HELLO, mine, { cache: CACHE });
    opts.onStatus('Waiting in Photon for another device');
  };

  client.onStateChange = (state) => {
    if (stopped || handed) return;
    if (state === 5) {
      client.joinRandomOrCreateRoom(
        { expectedMaxPlayers: 2, expectedCustomRoomProperties: { g: 'peasents' } },
        '',
        {
          maxPlayers: 2,
          isVisible: true,
          isOpen: true,
          customGameProperties: { g: 'peasents' },
          propsListedInLobby: ['g'],
          emptyRoomLiveTime: 15000,
        },
      );
    } else if (state === -1 || state === 10) {
      opts.onStatus('Photon could not connect. Still searching.');
    }
  };

  opts.onStatus('Connecting to Photon');
  client.connectToRegionMaster(REGION);
  return stop;
}

function photonLink(client: PhotonClient, localSide: Side, startAt: number, opponentCommander: string): NetLink {
  const inbox: NetCommand[] = [];
  let last = Date.now();
  const previous = client.onEvent;
  client.onEvent = (code, data, actorNr) => {
    previous(code, data, actorNr);
    if (actorNr === client.myActor().actorNr) return;
    if (code === PING) last = Date.now();
    if (code === CMD && data && typeof data === 'object') {
      inbox.push(data as NetCommand);
      last = Date.now();
    }
  };
  const ping = window.setInterval(() => {
    try {
      client.raiseEvent(PING, { t: Date.now() });
    } catch {
      /* socket already closed */
    }
  }, 1000);
  return {
    localSide,
    startAt,
    opponentCommander,
    send: (cmd) => client.raiseEvent(CMD, cmd),
    take: () => inbox.splice(0, inbox.length),
    silenceMs: () => Date.now() - last,
    close: () => {
      window.clearInterval(ping);
      try {
        client.disconnect();
      } catch {
        /* already closed */
      }
    },
  };
}
